import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { CATEGORY_LABELS, useHelpStore } from '@/stores/help-store'
import { useOnboardingStore } from '@/stores/onboarding-store'
import { HelpMarkdown } from '@/components/help/HelpMarkdown'

export function HelpPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { articles, articlesLoaded, fetchArticles, currentArticle, isLoadingArticle, articleError, fetchArticle } =
    useHelpStore()
  const { dismissedAt, fetchStatus: fetchOnboarding, restore: restoreOnboarding } = useOnboardingStore()
  const [filter, setFilter] = useState('')

  useEffect(() => {
    void fetchArticles()
  }, [fetchArticles])

  useEffect(() => {
    void fetchOnboarding()
  }, [fetchOnboarding])

  useEffect(() => {
    if (slug) void fetchArticle(slug)
  }, [slug, fetchArticle])

  const handleRestore = async () => {
    const restored = await restoreOnboarding()
    // A failed PATCH reverts dismissedAt in the store — stay put so the user
    // sees the Restore control still there rather than landing on a Home page
    // with no checklist.
    if (!restored) return
    // restore() only clears dismissedAt locally — refetch so the checklist
    // re-appears on Home with current ticks.
    void fetchOnboarding()
    void navigate('/app')
  }

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return articles
    // Title hits rank above summary hits, which rank above body-only hits.
    // Array.prototype.sort is stable, so within a rank the server's
    // category/order sequence survives.
    return articles
      .map((a) => {
        const rank = a.title.toLowerCase().includes(q)
          ? 0
          : a.description.toLowerCase().includes(q)
            ? 1
            : a.searchText?.toLowerCase().includes(q)
              ? 2
              : -1
        return { article: a, rank }
      })
      .filter((entry) => entry.rank >= 0)
      .sort((x, y) => x.rank - y.rank)
      .map((entry) => entry.article)
  }, [articles, filter])

  const byCategory = useMemo(() => {
    const groups = new Map<string, typeof filtered>()
    for (const key of Object.keys(CATEGORY_LABELS)) groups.set(key, [])
    for (const a of filtered) groups.get(a.category)?.push(a)
    return [...groups.entries()].filter(([, items]) => items.length > 0)
  }, [filtered])

  return (
    <div className="flex h-full min-h-0">
      <aside className="w-72 flex-shrink-0 overflow-y-auto border-r border-border p-4">
        <div className="relative mb-4">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search help"
            aria-label="Search help"
            className="h-9 rounded-lg bg-muted pl-8"
          />
        </div>
        {dismissedAt && (
          <div className="mb-4 px-2">
            <p className="text-meta">Getting Started checklist</p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-1 h-7 px-2 text-xs"
              onClick={() => void handleRestore()}
            >
              Restore to home page
            </Button>
          </div>
        )}
        {byCategory.map(([category, items]) => (
          <div key={category} className="mb-4">
            <div className="px-2 py-1 text-xs font-medium text-foreground">
              {CATEGORY_LABELS[category] ?? category}
            </div>
            <nav>
              {items.map((a) => {
                const isActive = a.slug === slug
                return (
                  <button
                    key={a.slug}
                    onClick={() => void navigate(`/app/help/${a.slug}`)}
                    className={cn(
                      'block w-full truncate rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                      isActive
                        ? 'bg-foreground/10 font-medium text-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    )}
                  >
                    {a.title}
                  </button>
                )
              })}
            </nav>
          </div>
        ))}
        {articlesLoaded && filtered.length === 0 && (
          <p className="px-2 text-sm text-muted-foreground">No articles match your search.</p>
        )}
      </aside>

      <main className="min-w-0 flex-1 overflow-y-auto">
        {!slug && (
          <div className="max-w-3xl px-10 py-8">
            <h1 className="text-sm font-medium">Help</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Guides for getting the most out of BidContext.
            </p>
            <div className="mt-6 space-y-6">
              {byCategory.map(([category, items]) => (
                <section key={category}>
                  <h2 className="text-sm font-medium">{CATEGORY_LABELS[category] ?? category}</h2>
                  <ul className="mt-2 space-y-2">
                    {items.map((a) => (
                      <li key={a.slug}>
                        <Link to={`/app/help/${a.slug}`} className="text-sm text-[hsl(var(--link))] hover:underline">
                          {a.title}
                        </Link>
                        <p className="text-meta">{a.description}</p>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        )}

        {slug && isLoadingArticle && (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading…</div>
        )}

        {slug && articleError && !isLoadingArticle && (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="text-sm text-muted-foreground">This article couldn't be loaded.</p>
            <Button variant="outline" onClick={() => void fetchArticle(slug)}>
              Try Again
            </Button>
          </div>
        )}

        {slug && currentArticle && !isLoadingArticle && (
          <div className="max-w-3xl px-10 py-8">
            <HelpMarkdown>{`# ${currentArticle.title}\n\n${currentArticle.body}`}</HelpMarkdown>
          </div>
        )}
      </main>
    </div>
  )
}
